import axios from 'axios';
import { useAuthStore } from '../store/useAuthStore';
import { isRequestCancellation, isTransientNetworkError } from './apiErrorClassification';
import i18n from './i18n';
import { getMessageApi } from './messageClient';

export { isRequestCancellation, isTransientNetworkError };

const notifyError = (content, duration = 2) => {
  const messageApi = getMessageApi();
  if (messageApi) {
    messageApi.error(content, duration);
  } else {
    console.error(content);
  }
};

// Système de gestion des erreurs réseau pour éviter le spam de notifications
let networkErrorShown = false;
let networkErrorTimeout = null;
const NETWORK_ERROR_COOLDOWN = 10000; // 10 secondes entre les notifications réseau

function isSessionSwapInProgress() {
  const { sessionTransitioning, sessionSilentSwap } = useAuthStore.getState();
  return sessionTransitioning || sessionSilentSwap;
}

/**
 * Affiche une notification d'erreur réseau avec debounce
 * Ne déclenche plus de toast/son, uniquement le bandeau orange
 */
const notifyNetworkError = () => {
  // Si une notification réseau a déjà été affichée récemment, ne pas en afficher une autre
  if (networkErrorShown) {
    return;
  }
  
  networkErrorShown = true;
  // Ne plus afficher de toast/son, uniquement le bandeau orange
  // notifyError(message, 5); // Désactivé pour éviter les sons
  
  // Déclencher un événement pour le NetworkStatusBanner
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('network-error'));
  }
  
  // Réinitialiser le flag après le cooldown
  if (networkErrorTimeout) {
    clearTimeout(networkErrorTimeout);
  }
  networkErrorTimeout = setTimeout(() => {
    networkErrorShown = false;
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('network-error-clear'));
    }
  }, NETWORK_ERROR_COOLDOWN);
};

export const NETWORK_ERROR_BANNER_COOLDOWN_MS = NETWORK_ERROR_COOLDOWN;

import { getNextjsApiBaseUrl } from '@/config/apiBaseUrl';
const service = axios.create({
  timeout: 50 * 1000,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
  },
});

// Envoie les cookies Supabase partagés (.folkyn.com) vers l'API
service.defaults.withCredentials = true;

// 自定义重试逻辑（保留配置字段，可由调用方自行实现）
service.defaults.retry = 1;
service.defaults.retryDelay = 1000;

// 请求拦截器
service.interceptors.request.use(
  async (config) => {
    config.baseURL = getNextjsApiBaseUrl();

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone) {
      config.headers['X-Client-Timezone'] = timezone;
    }

    // 添加多语言支持
    // en: English (default)
    // fr: French (Français)
    const currentLanguage = i18n.language || localStorage.getItem('i18nextLng') || 'en';
    config.headers['Accept-Language'] = currentLanguage;

    // Si FormData, laisser le navigateur définir le Content-Type avec le boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (err) => {
    return Promise.reject(err);
  }
);

// 响应拦截器
service.interceptors.response.use(
  (res) => {
    const { data, status, config } = res;
    const successCodes = [200, 201, 204];

    if (config?.responseType === 'blob' || config?.rawResponse) {
      return Promise.resolve(res);
    }

    if (!successCodes.includes(status)) {
      // Ne JAMAIS afficher de toast pour les erreurs serveur (500+)
      // Ces erreurs sont techniques et ne concernent pas l'utilisateur
      if (status >= 500) {
        console.error('[API Error]', status, res.data);
        return Promise.reject(res);
      }
      
      // Ignorer le message "not associated with a team/company" lors de l'onboarding
      const errorMessage = res.data?.message || '';
      const isOnboardingError = errorMessage.includes('not associated with a team') || 
                                errorMessage.includes('not associated with a team/company');
      const { sessionTransitioning, sessionSilentSwap } = useAuthStore.getState();
      const isSessionSwapInProgress = sessionTransitioning || sessionSilentSwap;

      if (!config.hideMessageModal && !isOnboardingError && !isSessionSwapInProgress) {
        notifyError(errorMessage, 2);
      }

      return Promise.reject(res);
    } else {
      if (!data || res.status === 204) return Promise.resolve(res);
      
      // Si c'est une string (fichier CSV/XML), retourner directement
      if (typeof data === 'string') {
        const contentType = res.headers?.['content-type'] || res.headers?.['Content-Type'] || ''
        if (contentType.includes('text/csv') || contentType.includes('application/xml') || contentType.includes('text/xml')) {
          return Promise.resolve(res)
        }
      }
      
      // Si ce n'est pas un objet, retourner directement (peut être un blob ou autre)
      if (typeof data !== 'object' || data === null) {
        return Promise.resolve(res)
      }
      
      const { code } = data;

      if (!successCodes.includes(code)) {
        // Ne JAMAIS afficher de toast pour les erreurs serveur (500+)
        // Ces erreurs sont techniques et ne concernent pas l'utilisateur
        if (code >= 500) {
          console.error('[API Error]', code, data);
          return Promise.reject(res);
        }
        
        // Ignorer le message "not associated with a team/company" lors de l'onboarding
        const errorMessage = data.message || '';
        const isOnboardingError = errorMessage.includes('not associated with a team') || 
                                  errorMessage.includes('not associated with a team/company');
        const { sessionTransitioning, sessionSilentSwap } = useAuthStore.getState();
        const isSessionSwapInProgress = sessionTransitioning || sessionSilentSwap;

        if (!config.hideMessageModal && !isOnboardingError && !isSessionSwapInProgress) {
          notifyError(errorMessage, 2);
        }
        return Promise.reject(res);
      }
      return Promise.resolve(res);
    }
  },
  (error) => {
    const status = error?.response?.status;
    if (status === 204) {
      return Promise.resolve(error.response);
    }
    
    // Gérer les erreurs réseau de manière centralisée
    if (isTransientNetworkError(error)) {
      if (
        !error?.config?.hideMessageModal &&
        !isRequestCancellation(error) &&
        !isSessionSwapInProgress()
      ) {
        notifyNetworkError();
      }
      return Promise.reject(error);
    }
    
    if ([401, 403].includes(status)) {
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      const isPublicAuth =
        path.startsWith('/login') ||
        path.startsWith('/sign-on') ||
        path.startsWith('/auth/callback') ||
        path.startsWith('/forgot-password') ||
        path.startsWith('/reset-password');

      const requestUrl = String(error?.config?.url || '');
      const isImpersonationAuthRoute =
        requestUrl.includes('/super-admin/stop-impersonate') ||
        requestUrl.includes('/super-admin/impersonate');

      if (status === 401 && !isPublicAuth) {
        const { sessionHydrated, impersonating, sessionTransitioning, sessionSilentSwap } = useAuthStore.getState();
        const isSessionSwapInProgress = sessionTransitioning || sessionSilentSwap;
        // Ne pas effacer pendant bootstrap, impersonation, transition session, ou flux impersonation API
        if (!sessionHydrated || impersonating || isSessionSwapInProgress || isImpersonationAuthRoute) {
          return Promise.reject(error);
        }
        // Passive 401 recovery must NOT POST /auth/logout: a stale concurrent 401
        // (or a late response after re-login) would wipe a freshly established cookie session.
        void import('@/lib/auth/clearStaleAuthState').then(({ clearStaleAuthState }) =>
          clearStaleAuthState({ callLogoutApi: false })
        );
      }

      return Promise.reject(error);
    }
    
    // Ne JAMAIS afficher de toast pour les erreurs serveur (500+)
    // Ces erreurs sont techniques et ne concernent pas l'utilisateur
    // Surtout avec une connexion instable, ça spam les toasts
    if (status >= 500) {
      // Log en console pour le debug, mais pas de toast pour l'utilisateur
      console.error('[API Error]', status, error?.response?.data);
      return Promise.reject(error);
    }
    
    // Ignorer le message "not associated with a team/company" lors de l'onboarding
    // C'est un cas normal et attendu pour un nouvel onboarding
    const errorMessage = error?.response?.data?.message || '';
    const isOnboardingError = errorMessage.includes('not associated with a team') || 
                              errorMessage.includes('not associated with a team/company');
    const requestUrl = String(error?.config?.url || '');
    const isRegisterEmailBlockError =
      status === 400 &&
      (errorMessage === 'PROFESSIONAL_EMAIL_REQUIRED' ||
        errorMessage === 'DISPOSABLE_EMAIL_NOT_ALLOWED') &&
      requestUrl.includes('/api/app/v1/auth/register');

    const { sessionTransitioning, sessionSilentSwap } = useAuthStore.getState();
    const isSessionSwapInProgress = sessionTransitioning || sessionSilentSwap;

    if (!error?.config?.hideMessageModal && 
        error.name !== 'CanceledError' && 
        error.name !== 'AbortError' &&
        !isOnboardingError &&
        !isRegisterEmailBlockError &&
        !isSessionSwapInProgress) {
      notifyError(errorMessage || 'An error occurred', 2);
    }
    return Promise.reject(error);
  }
);

// 发送请求
export function post(url, params, config = {}) {
  return new Promise((resolve, reject) => {
    service
      .post(url, params, config)
      .then((res) => {
        resolve(config?.rawResponse ? res : res.data);
      })
      .catch((err) => {
        reject(err);
      });
  });
}
export function get(url, params, config = {}) {
  return new Promise((resolve, reject) => {
    service
      .get(
        url,
        {
          params: params,
          ...config,
        }
      )
      .then((res) => {
        resolve(res.data);
      })
      .catch((err) => {
        reject(err);
      });
  });
}

export function Put(url, params, config) {
  return new Promise((resolve, reject) => {
    service
      .put(url, { ...params }, config)
      .then((res) => {
        resolve(res.data);
      })
      .catch((err) => {
        reject(err);
      });
  });
}

export function patch(url, params, config = {}) {
  return new Promise((resolve, reject) => {
    service
      .patch(url, params, config)
      .then((res) => {
        resolve(config?.rawResponse ? res : res.data);
      })
      .catch((err) => {
        reject(err);
      });
  });
}

export function Delete(url, params, config = {}) {
  return new Promise((resolve, reject) => {
    service
      .delete(url, { ...config, params, data: config.data })
      .then((res) => {
        resolve(config?.rawResponse ? res : res.data);
      })
      .catch((err) => {
        reject(err);
      });
  });
}

// 兼容旧版默认导出
service.postJSON = post;
service.getJSON = get;
service.putJSON = Put;
service.deleteJSON = Delete;

export default service;
